import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  query,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import type { CartItem } from '@/types/order'

const MAX_CART_ITEMS = 50

// Get the cart items subcollection ref for a user
function cartRef(userId: string) {
  return collection(db, 'carts', userId, 'items')
}

// Add item to cart (or increment quantity if already exists)
export async function addToCart(
  userId: string,
  item: {
    listingId: string
    sellerId: string
    price: number
    title: string
    image: string
    quantity?: number
  }
): Promise<void> {
  const itemsRef = cartRef(userId)

  // Check if this listing is already in cart
  const existingSnap = await getDocs(itemsRef)
  const existing = existingSnap.docs.find(
    (d) => d.data().listingId === item.listingId
  )

  if (existing) {
    // Increment quantity
    const currentQty = existing.data().quantity || 1
    await updateDoc(existing.ref, {
      quantity: currentQty + (item.quantity || 1),
    })
    return
  }

  // Check cart size limit
  if (existingSnap.size >= MAX_CART_ITEMS) {
    throw new Error(`Cart is full (max ${MAX_CART_ITEMS} items)`)
  }

  // Add new item
  const newRef = doc(itemsRef)
  await setDoc(newRef, {
    listingId: item.listingId,
    sellerId: item.sellerId,
    quantity: item.quantity || 1,
    price: item.price,
    title: item.title,
    image: item.image,
    addedAt: serverTimestamp(),
  })
}

// Update cart item quantity
export async function updateCartItemQuantity(
  userId: string,
  itemId: string,
  quantity: number
): Promise<void> {
  if (quantity < 1) {
    await removeFromCart(userId, itemId)
    return
  }
  const itemRef = doc(db, 'carts', userId, 'items', itemId)
  await updateDoc(itemRef, { quantity })
}

// Remove item from cart
export async function removeFromCart(
  userId: string,
  itemId: string
): Promise<void> {
  const itemRef = doc(db, 'carts', userId, 'items', itemId)
  await deleteDoc(itemRef)
}

// Clear entire cart
export async function clearCart(userId: string): Promise<void> {
  const itemsRef = cartRef(userId)
  const snap = await getDocs(itemsRef)
  const deletes = snap.docs.map((d) => deleteDoc(d.ref))
  await Promise.all(deletes)
}

// Clear items from a specific seller (after checkout)
export async function clearCartBySeller(
  userId: string,
  sellerId: string
): Promise<void> {
  const itemsRef = cartRef(userId)
  const snap = await getDocs(itemsRef)
  const deletes = snap.docs
    .filter((d) => d.data().sellerId === sellerId)
    .map((d) => deleteDoc(d.ref))
  await Promise.all(deletes)
}

// Subscribe to cart items (real-time)
export function subscribeToCart(
  userId: string,
  callback: (items: CartItem[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const itemsRef = query(cartRef(userId))

  return onSnapshot(
    itemsRef,
    (snapshot) => {
      const items: CartItem[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as CartItem[]
      callback(items)
    },
    (error) => {
      console.error('Cart subscription error:', error)
      onError?.(error)
    }
  )
}

// Get cart items once (non-realtime)
export async function getCartItems(userId: string): Promise<CartItem[]> {
  const itemsRef = cartRef(userId)
  const snap = await getDocs(itemsRef)
  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as CartItem[]
}
